import type { ActionContext } from './types';
import type { ProjectileModifiers, HitEffectEntry, ElementType } from './modifiers/types';
import { gameLogger } from '../../../../utils/GameLogger';
import type { AimCategory } from '../../../../shared/aiming/aimMetadata';

import type { CastGeometry, OrbitGeometry } from '../../../effects/castGeometry/castGeometry';
import { projectileGeometryOf } from '../../../effects/castGeometry/projectileGeometry';

export type AbilityFunction = (ctx: ActionContext) => void;

export interface HomingMetadata {
  strength: number;
  maxTurnRate: number;
  delay: number;
}

export interface ProjectileMetadata {
  damage: number | string; // Numeric or 'Scaling', 'Dynamic', etc.
  speed: number;
  shape?: 'star' | 'rect' | 'circle' | 'card' | 'arrow' | 'crescent' | 'spiral' | 'beam' | 'hammer';
  color?: number;
  width?: number;
  height?: number;
  rotationSpeed?: number;
  gravity?: number;
  lifespan?: number;
  homing?: HomingMetadata;
  strokeColor?: number;
  strokeWidth?: number;
  modifiers?: ProjectileModifiers;
  trajectory?: 'linear' | 'sine' | 'spiral' | 'zigzag';
  trajectoryAmplitude?: number;
  trajectoryFrequency?: number;
  /** Deterministic total vertical fan span for multi-projectile casts. */
  spreadY?: number;
}

export interface VFXMetadata {
  windUp?: string;
  cast?: string;
  muzzleFlash?: string;
  color?: number;
  scale?: number;
  duration?: number;
  trail?: string;
  impact?: string;
  aura?: string;
}

export interface MeleeMetadata {
  arcAngle?: number;       // Start angle offset in degrees (default -45)
  arcSpread?: number;      // Total arc sweep in degrees (default 90)
  arcRadius?: number;      // Swing radius in pixels (default 60)
  swingDuration?: number;  // Tween duration ms (default 150)
  lineWidth?: number;      // Arc line width (default 4)
  lineColor?: number;      // Arc color override
  hitRange?: number;       // Damage detection range in pixels
  knockback?: number;      // Knockback distance in pixels
}

export interface BuffLayer {
  stat: string;
  value: number;
  duration: number;
  vfxId?: string;
}

export interface BehaviorConfig {
  type: 'projectile' | 'multi_projectile' | 'aoe' | 'cone_aoe' | 'line_aoe' | 'channel' | 'buff' | 'dash' | 'heal' | 'shield' | 'debuff' | 'melee' | 'hitscan' | 'summon' | 'nova' | 'aura' | 'passive' | 'toggle' | 'instant';
  params?: Record<string, any>;
}

export interface AbilityMetadata {
  reachTuned?: boolean;
  unsupportedDelivery?: boolean;
  id: string;
  name?: string;
  description?: string;
  fn: AbilityFunction;
  /** Explicit input/delivery override; projectile assistance is the default. */
  aimCategory?: AimCategory;
  target?: string;
  tags?: string[];
  manaCost?: number;
  cooldown?: number;
  castTime?: number;
  icon?: string;
  projectile?: ProjectileMetadata;
  vfx?: VFXMetadata;
  melee?: MeleeMetadata;
  buffLayers?: BuffLayer[];
  behaviors?: BehaviorConfig[];
  /** What a cast draws (plan 29 F5) — described at build from the builder configs. */
  castGeometry?: CastGeometry;
  /** A declared orbit (Lucky Seven) folded into castGeometry. */
  castOrbit?: OrbitGeometry;
  element?: ElementType;
  onHitEffects?: HitEffectEntry[];
  /** Internal: stored damage function set by AbilityBuilder.withProjectile() for dynamic scaling */
  _damageFn?: (ctx: ActionContext) => number;
  damageFormula?: number | ((ctx: ActionContext) => number);
  range?: number;
  castOptions?: { interruptible?: boolean; movementCancels?: boolean; vfxId?: string };
  chainInto?: { abilityId: string; windowMs: number };
  isPassive?: boolean;
  isToggle?: boolean;
  charges?: { maxCharges: number; regenTimeMs: number; consumePerCast: number };
  damageType?: 'physical' | 'magical' | 'true';
  groundTarget?: { maxRange: number; indicatorRadius?: number; indicatorColor?: number };
  ranks?: Array<{ rank: number; damageMultiplier?: number; cooldownMs?: number; manaCost?: number; radiusBonus?: number }>;
  maxRank?: number;
  resource?: { type: string; cost?: number; generate?: number };
  timeline?: Array<{ at: number; type: string; effectId?: string; intensity?: number; duration?: number; color?: number; abilityId?: string }>;
}

/**
 * AbilityRegistry - Data-driven ability system
 *
 * Abilities register themselves at module load time.
 * No hardcoded maps needed.
 */
type TomlResolver = (id: string) => AbilityFunction | undefined;
/** Served icon path for an ability id, or undefined when none is authored. */
type IconResolver = (id: string) => string | undefined;

class AbilityRegistry {
  private abilities = new Map<string, AbilityMetadata>();
  private autoAttacks = new Map<string, AbilityFunction>();
  private tomlResolver: TomlResolver | null = null;
  private iconResolver: IconResolver | null = null;
  private version = 0;
  private listeners = new Set<() => void>();
  private notifyQueued = false;

  /**
   * Subscribe to registry changes (register / updateMetadata / icon wiring).
   * UI that renders registry metadata (the hotbar) subscribes so a class that
   * registers after its first render — boot registers the class after the
   * HUD mounts — repaints with the real icon. Notifications are coalesced
   * into one microtask, so a boot burst of registrations is one update and a
   * lazy registration during a render never updates state mid-render.
   */
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  /** Monotonic change counter — the `useSyncExternalStore` snapshot. */
  getVersion = (): number => this.version;

  private changed(): void {
    this.version++;
    if (this.notifyQueued) return;
    this.notifyQueued = true;
    queueMicrotask(() => {
      this.notifyQueued = false;
      for (const listener of [...this.listeners]) listener();
    });
  }

  /**
   * Set the authored-icon resolver and apply it to every registered ability.
   * Later registrations get their icon in `register`, so an ability that
   * registers after boot (another class, a lazy TOML build) is not left on
   * its emoji or a stale path.
   */
  setIconResolver(resolver: IconResolver): void {
    this.iconResolver = resolver;
    for (const [id, metadata] of this.abilities) {
      const icon = resolver(id);
      if (icon && metadata.icon !== icon) this.abilities.set(id, { ...metadata, icon });
    }
    this.changed();
  }

  /**
   * Register an ability with the system
   */
  register(metadata: AbilityMetadata): void {
    const finalMetadata = { ...metadata };

    // If the function has attached metadata (from AbilityBuilder), merge it in
    // This allows index.ts to only provide tags/overrides while the builder
    // provides the core gameplay data (cooldown, homing, etc.)
    const fnMetadata = (metadata.fn as any).metadata;
    if (fnMetadata) {
      Object.assign(finalMetadata, fnMetadata, metadata); // metadata overrides fnMetadata
    }

    const existing = this.abilities.get(finalMetadata.id);
    // Scene/HMR re-entry legitimately invokes a class registrar again with
    // the same module-level function. Refresh its metadata silently; only a
    // genuinely different implementation deserves the overwrite warning.
    if (existing && existing.fn !== finalMetadata.fn) {
      gameLogger.warn('abilities', `Ability "${finalMetadata.id}" already registered, overwriting`);
    }
    // Plan 29 F5 (lane BY): the caster's final projectile joins the cast
    // geometry observers draw from — the metadata the behaviours fire from.
    const projectile = projectileGeometryOf(finalMetadata, finalMetadata.castGeometry?.volley);
    if (projectile) finalMetadata.castGeometry = { ...finalMetadata.castGeometry, projectile };
    const authoredIcon = this.iconResolver?.(finalMetadata.id);
    if (authoredIcon) finalMetadata.icon = authoredIcon;
    this.abilities.set(finalMetadata.id, finalMetadata);
    this.changed();
  }

  /**
   * Register a class's auto-attack function
   */
  registerAutoAttack(className: string, fn: AbilityFunction): void {
    this.autoAttacks.set(className.toLowerCase(), fn);
  }

  /**
   * Set the lazy TOML ability resolver. Called once by TomlAbilityFactory.
   */
  setTomlResolver(resolver: TomlResolver): void {
    this.tomlResolver = resolver;
  }

  /**
   * Get an ability function by ID. Falls back to the lazy TOML resolver for
   * abilities that have not been eagerly registered yet.
   */
  get(id: string): AbilityFunction | undefined {
    const existing = this.abilities.get(id);
    if (existing) return existing.fn;
    if (this.tomlResolver) {
      const built = this.tomlResolver(id);
      if (built) return built;
    }
    return undefined;
  }

  /**
   * Get auto-attack function for a class
   */
  getAutoAttack(className: string): AbilityFunction | undefined {
    return this.autoAttacks.get(className.toLowerCase());
  }

  /**
   * Check if ability exists. Falls back to the lazy TOML resolver.
   */
  has(id: string): boolean {
    if (this.abilities.has(id)) return true;
    if (this.tomlResolver) {
      const built = this.tomlResolver(id);
      return built !== undefined;
    }
    return false;
  }

  /**
   * Internal check: ability is already in the registry (does not trigger lazy
   * resolution). Useful for resolvers that need to avoid re-entrancy.
   */
  isRegistered(id: string): boolean {
    return this.abilities.has(id);
  }

  /**
   * Get all registered ability IDs
   */
  getAllIds(): string[] {
    return Array.from(this.abilities.keys());
  }

  /**
   * Get all registered ability metadata
   */
  getAll(): AbilityMetadata[] {
    return Array.from(this.abilities.values());
  }

  /**
   * Get metadata for an ability. Falls back to the lazy TOML resolver.
   */
  getMetadata(id: string): AbilityMetadata | undefined {
    const existing = this.abilities.get(id);
    if (existing) return existing;
    if (this.tomlResolver) {
      const built = this.tomlResolver(id);
      if (built) return this.abilities.get(id);
    }
    return undefined;
  }

  /**
   * Update metadata for an ability (used by UI sliders for live editing)
   */
  updateMetadata(id: string, patch: Partial<AbilityMetadata>): void {
    const existing = this.abilities.get(id);
    if (!existing) return;

    // Deep merge for all nested objects
    const updated = { ...existing, ...patch };
    const nestedKeys: (keyof AbilityMetadata)[] = ['projectile', 'vfx', 'melee', 'charges', 'castOptions', 'chainInto', 'groundTarget', 'resource'];
    for (const key of nestedKeys) {
      if (patch[key] && existing[key] && typeof patch[key] === 'object' && typeof existing[key] === 'object') {
        (updated as any)[key] = { ...(existing as any)[key], ...(patch as any)[key] };
      }
    }
    // Extra deep merge for projectile.homing and projectile.modifiers
    if (patch.projectile && existing.projectile) {
      if (patch.projectile.homing && existing.projectile.homing) {
        updated.projectile!.homing = { ...existing.projectile.homing, ...patch.projectile.homing };
      }
      if ((patch.projectile as any).modifiers && (existing.projectile as any).modifiers) {
        (updated.projectile as any).modifiers = { ...(existing.projectile as any).modifiers, ...(patch.projectile as any).modifiers };
      }
    }

    this.abilities.set(id, updated);
    this.changed();
  }

  /**
   * Collect ALL VFX IDs referenced by an ability (from vfx metadata, behaviors, windUp, etc.)
   * Single API for validation — used by contract dashboard and CI tests.
   */
  vfxIds(id: string): string[] {
    const meta = this.abilities.get(id);
    if (!meta) return [];
    const ids = new Set<string>();

    // Top-level VFX metadata
    if (meta.vfx) {
      if (meta.vfx.muzzleFlash) ids.add(meta.vfx.muzzleFlash);
      if (meta.vfx.windUp) ids.add(meta.vfx.windUp);
      if (meta.vfx.cast) ids.add(meta.vfx.cast);
      if (meta.vfx.aura) ids.add(meta.vfx.aura);
      if (meta.vfx.trail) ids.add(meta.vfx.trail);
      if (meta.vfx.impact) ids.add(meta.vfx.impact);
    }

    // Cast options VFX
    if (meta.castOptions?.vfxId) ids.add(meta.castOptions.vfxId);

    // Behavior-level VFX (melee, AOE, summon, buff vfxIds in params)
    if (meta.behaviors) {
      for (const b of meta.behaviors) {
        if (b.params?.vfxId) ids.add(b.params.vfxId);
        if (b.params?.vfx?.effectId) ids.add(b.params.vfx.effectId);
        if (b.params?.vfxTrigger) ids.add(b.params.vfxTrigger);
      }
    }

    // Timeline VFX entries
    if (meta.timeline) {
      for (const entry of meta.timeline) {
        if (entry.effectId) ids.add(entry.effectId);
      }
    }

    // Buff layers VFX
    if (meta.buffLayers) {
      for (const layer of meta.buffLayers) {
        if (layer.vfxId) ids.add(layer.vfxId);
      }
    }

    return Array.from(ids);
  }

  /**
   * Get all abilities matching a tag
   */
  getByTag(tag: string): AbilityMetadata[] {
    return Array.from(this.abilities.values()).filter(a => a.tags?.includes(tag));
  }

  /**
   * Get all abilities for a specific character class, including general/basic ones.
   */
  getAbilitiesForClass(className: string): AbilityMetadata[] {
    const classTag = className.toLowerCase();
    return Array.from(this.abilities.values()).filter(a =>
      a.tags?.includes(classTag) ||
      a.tags?.includes('general') ||
      a.tags?.includes('basic')
    );
  }
}

// Singleton instance
export const abilityRegistry = new AbilityRegistry();
